import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { buildTerrain, cellId, DIRS, hexDist, hexToWorld, type DungeonLayout, type MapDef } from '@somnhold/sim';
import { emis, glowMat, metal, std, stone } from '../materials.ts';
import { simToWorld, WORLD_PER_HEX } from './coords.ts';
import { exitShaft } from './deep.ts';
import { mulberry, type TerrainBuild } from './terrain.ts';

const hash2 = (q: number, r: number): number => {
  let h = Math.imul(q * 374761393 + r * 668265263, 0x27d4eb2d);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
};

const WALL_H = 0.72;


/**
 * Pieces of one material placed in a common frame (their own position and turn), as one mesh: a door's frame or its
 * bars, a room's sconces. Each piece was a draw call of its own, a portcullis twenty of them, most of a crypt room's.
 */
/** `o` is `top` or somewhere under it. */
const isUnder = (o: THREE.Object3D, top: THREE.Object3D): boolean => {
  for (let x: THREE.Object3D | null = o; x; x = x.parent) if (x === top) return true;
  return false;
};

const oneMesh = (pieces: THREE.Mesh[]): THREE.Mesh => {
  const geos = pieces.map((m) => {
    m.updateMatrix();
    return m.geometry.clone().applyMatrix4(m.matrix);
  });
  const merged = mergeGeometries(geos, false) as THREE.BufferGeometry;
  for (const g of [...geos, ...pieces.map((m) => m.geometry)]) g.dispose();
  return new THREE.Mesh(merged, pieces[0]!.material);
};
/** The Furrows (map.look 'furrows'): cold stone and moss; the floor pale flags, the walls dark rock and taller. */
const F = {
  flagA: new THREE.Color('#6a6d74'),
  flagB: new THREE.Color('#5d6067'),
  flagC: new THREE.Color('#74777e'),
  grout: new THREE.Color('#1a1b1f'),
  rockTop: new THREE.Color('#25282d'),
  rockTopB: new THREE.Color('#212428'),
  moss: new THREE.Color('#2f3c26'),
  mossD: new THREE.Color('#25301f'),
  side: new THREE.Color('#25282d'),
  foot: new THREE.Color('#0e0f11'),
  crack: new THREE.Color('#121316'),
};
/** How far past the labyrinth the rock goes on (cells), so there is never an edge to the world on screen. */
const FURROWS_RIM = 7;

const C = {
  slateA: new THREE.Color('#3b3e45'),
  slateB: new THREE.Color('#33363c'),
  slateMoss: new THREE.Color('#34402f'),
  hall: new THREE.Color('#2c2a33'),
  grout: new THREE.Color('#141518'),
  wallTop: new THREE.Color('#41444c'),
  wallTopB: new THREE.Color('#373a41'),
  wallSide: new THREE.Color('#24262b'),
  wallFoot: new THREE.Color('#0d0e10'),
};

export interface CryptDoor {
  /** Door index (leads into room index + 1). */
  i: number;
  bars: THREE.Group;
  seal: THREE.Sprite;
  pos: THREE.Vector3;
  /** 0 shut … 1 raised; eased toward the target every frame. */
  open: number;
}

export interface CryptBuild extends TerrainBuild {
  doors: CryptDoor[];
  /** Flames on the walls (flicker), by room. */
  flames: { mesh: THREE.Object3D; glow: THREE.Sprite; room: number }[];
  /** Overhead light spots per room (warm), the boss hall's in violet. */
  roomLights: THREE.Vector3[];
  /** The rune circle in the Warden's hall (pulses); in the deep crypt one in every guardian's hall, each a child group. */
  runes: THREE.Group;
  /** Deep crypt (level III): the floor of each room (0 stone, 1 embers, 2 obsidian); all 0 in the other crypts. */
  floors: number[];
  /** The crypt has more than one floor (the deep crypt). */
  deep: boolean;
  /** A saga descent without a Warden: the last hall is the way out, a shaft of pale light (its uniform ticks the shimmer). */
  exit?: { group: THREE.Group; time: THREE.IUniform };
}

/**
 * The crypt as a diorama: hex flagstones with dark grout, walls raised on every cell that borders
 * the floor, sconces with flames on room walls, portcullises in each corridor and a violet rune
 * circle under the Warden. With `exitHall` (a saga descent without a Warden) the last hall is a plain
 * room with the way out in its middle instead.
 */
export const buildCrypt = (map: MapDef, seed: number, exitHall = false): CryptBuild => {
  const L = map.dungeon as DungeonLayout;
  const R = map.radius;
  const terrain = buildTerrain(map);
  const W = 2 * R + 1;
  const heights = new Float32Array(W * W);
  // A grid, not a set of "q,r" strings: building those keys was a fifth of a Chasm floor change (client-perf
  // 2026-11-03: 100–290 ms per floor on slow machines). A cell off the map (none today) is kept by its key.
  const grid = new Uint8Array(W * W);
  const off = new Set<string>();
  const inMap = (q: number, r: number): boolean => q >= -R && q <= R && r >= -R && r <= R;
  for (const h of L.floor) {
    if (inMap(h.q, h.r)) grid[(h.q + R) * W + h.r + R] = 1;
    else off.add(`${h.q},${h.r}`);
  }
  const isFloor = (q: number, r: number): boolean => (inMap(q, r) ? grid[(q + R) * W + r + R] === 1 : off.size > 0 && off.has(`${q},${r}`));
  const roomOf = (q: number, r: number): number => {
    for (let i = 0; i < L.rooms.length; i++) {
      const rm = L.rooms[i]!;
      const dq = q - rm.q;
      const dr = r - rm.r;
      if ((Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2 <= rm.radius) return i;
    }
    return -1;
  };
  const boss = L.rooms.length - 1;
  const floors = L.rooms.map((rm) => rm.floor ?? 0);
  const deep = floors.some((f) => f > 0);
  /** A guardian's hall: the last room, and in the deep crypt the last room of every floor. */
  const isHall = (i: number): boolean => (i === boss && !exitHall) || (deep && L.rooms[i]?.kind === 'boss');
  /** The floor a cell belongs to: its room's, or the nearest room's (corridors). */
  const floorAt = (q: number, r: number): number => {
    let best = 0;
    let bd = Infinity;
    for (let i = 0; i < L.rooms.length; i++) {
      const rm = L.rooms[i]!;
      const dq = q - rm.q;
      const dr = r - rm.r;
      const d = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2 - rm.radius;
      if (d < bd) {
        bd = d;
        best = floors[i] ?? 0;
      }
    }
    return best;
  };
  const fl: number[] = [];
  let curFloor = 0;
  const S = WORLD_PER_HEX;
  const rad = S / Math.sqrt(3);
  const corners: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    corners.push([Math.cos(a) * rad, Math.sin(a) * rad]);
  }
  const pos: number[] = [];
  const col: number[] = [];
  const tri = (a: number[], b: number[], c: number[], ca: THREE.Color, cb: THREE.Color, cc: THREE.Color): void => {
    pos.push(...a, ...b, ...c);
    fl.push(curFloor, curFloor, curFloor);
    col.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
  };
  const prism = (cx: number, cz: number, h: number, inset: number, top: THREE.Color, side: THREE.Color, foot: THREE.Color, bottom = -0.4, sides = true): void => {
    const ring = corners.map(([x, z]) => [cx + x * inset, h, cz + z * inset]);
    const center = [cx, h, cz];
    for (let i = 0; i < 6; i++) {
      const a = ring[i] as number[];
      const b = ring[(i + 1) % 6] as number[];
      const shade = top.clone().multiplyScalar(0.93 + hash2(Math.round(cx * 10) + i, Math.round(cz * 10)) * 0.14);
      tri(center, b, a, top, shade, shade);
      // Inside the rock no side shows (every neighbour stands as high): the top alone.
      if (!sides) continue;
      const a2 = [a[0] as number, bottom, a[2] as number];
      const b2 = [b[0] as number, bottom, b[2] as number];
      tri(a, b, b2, side, side, foot);
      tri(a, b2, a2, side, foot, foot);
    }
  };
  const walls: { q: number; r: number; room: number; x: number; z: number; h?: number }[] = [];
  const furrows = map.look === 'furrows';
  /** A thin dark crack across a stone top, from (x0, z0) to (x1, z1). */
  const crack = (x0: number, z0: number, x1: number, z1: number, y: number, w: number): void => {
    const dx = x1 - x0;
    const dz = z1 - z0;
    const l = Math.hypot(dx, dz) || 1;
    const nx = (-dz / l) * w;
    const nz = (dx / l) * w;
    const a = [x0 + nx, y, z0 + nz];
    const b = [x1 + nx, y, z1 + nz];
    const c2 = [x1 - nx, y, z1 - nz];
    const d = [x0 - nx, y, z0 - nz];
    tri(a, c2, b, F.crack, F.crack, F.crack);
    tri(a, d, c2, F.crack, F.crack, F.crack);
  };
  const RR = furrows ? R + FURROWS_RIM : R;
  for (let r = -RR; r <= RR; r++) {
    for (let q = -RR; q <= RR; q++) {
      if (furrows && (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2 > RR) continue;
      const c = hexToWorld(q, r);
      const cx = (c.x / 1000) * S;
      const cz = (c.y / 1000) * S;
      const n = hash2(q, r);
      curFloor = deep ? floorAt(q, r) : 0;
      if (furrows) {
        const id = cellId(R, q, r);
        if (isFloor(q, r)) {
          // The furrow's floor: one even, pale surface; along a corridor, its flags' joints run across it, so the way
          // reads as a path at a glance.
          if (id >= 0) heights[id] = 0;
          prism(cx, cz, -0.035, 1, F.grout, F.grout, F.grout, -0.08);
          const tone = F.flagA.clone().lerp(F.flagC, hash2(q * 7, r * 11) * 0.5).multiplyScalar(0.96 + hash2(q, r * 3) * 0.06);
          prism(cx, cz, 0, 0.99, tone, tone.clone().multiplyScalar(0.6), F.grout, -0.05);
          const open = DIRS.map((d, k) => (isFloor(q + d.q, r + d.r) ? k : -1)).filter((k) => k >= 0);
          if (open.length === 2 && (open[0]! + 3) % 6 === open[1]!) {
            const d = DIRS[open[0]!]!;
            const w0 = hexToWorld(q + d.q, r + d.r);
            const ax = (w0.x / 1000) * S - cx;
            const az = (w0.y / 1000) * S - cz;
            const l = Math.hypot(ax, az) || 1;
            const ux = ax / l;
            const uz = az / l;
            for (const t of [-0.25, 0.25]) {
              const mx = cx + ux * t * S;
              const mz = cz + uz * t * S;
              crack(mx - uz * rad * 0.85, mz + ux * rad * 0.85, mx + uz * rad * 0.85, mz - ux * rad * 0.85, 0.003, 0.012);
            }
          }
          continue;
        }
        // Rock: the furrow's walls, one even plateau of dark stone a head taller than a man, darker down the sides
        // that face a corridor; moss and roots on those faces, and now and then a crack on top.
        let touches = -1;
        for (const d of DIRS) {
          if (isFloor(q + d.q, r + d.r)) {
            touches = roomOf(q + d.q, r + d.r);
            if (touches < 0) touches = -2;
            break;
          }
        }
        const h = 1.55 + n * 0.04;
        if (id >= 0) heights[id] = h;
        const mossy = hash2(q * 3, r * 7) < 0.55;
        prism(cx, cz, h, 1.02, n < 0.5 ? F.rockTop : F.rockTopB, mossy && touches !== -1 ? F.moss : F.side, F.foot, -0.4, touches !== -1);
        if (touches !== -1 && n > 0.6) {
          const a0 = hash2(r, q) * Math.PI * 2;
          crack(cx + Math.cos(a0) * rad * 0.7, cz + Math.sin(a0) * rad * 0.7, cx - Math.cos(a0 + 0.4) * rad * 0.6, cz - Math.sin(a0 + 0.4) * rad * 0.6, h + 0.002, 0.012);
        }
        if (touches !== -1) walls.push({ q, r, room: touches, x: cx, z: cz, h });
        continue;
      }
      if (isFloor(q, r)) {
        const room = roomOf(q, r);
        const hall = room >= 0 && isHall(room);
        const base = hall ? C.hall : n < 0.5 ? C.slateA : C.slateB;
        const top = base.clone().lerp(C.slateMoss, hall ? 0 : n * n * 0.5).multiplyScalar(0.9 + n * 0.18);
        const h = (hash2(q * 3, r * 5) - 0.5) * 0.025;
        const id = cellId(R, q, r);
        if (id >= 0) heights[id] = 0;
        prism(cx, cz, h, 0.94, top, top.clone().multiplyScalar(0.5), C.grout, -0.08);
        // Grout around the stones.
        prism(cx, cz, -0.035, 1, C.grout, C.grout, C.grout, -0.08);
        continue;
      }
      // A wall wherever this cell touches the floor.
      let touches = -1;
      for (const d of DIRS) {
        if (isFloor(q + d.q, r + d.r)) {
          touches = roomOf(q + d.q, r + d.r);
          if (touches < 0) touches = -2;
          break;
        }
      }
      if (touches === -1) continue;
      const h = WALL_H + n * 0.18 + (touches >= 0 && isHall(touches) ? 0.25 : 0);
      prism(cx, cz, h, 1, n < 0.5 ? C.wallTop : C.wallTopB, C.wallSide, C.wallFoot);
      walls.push({ q, r, room: touches, x: cx, z: cz });
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  if (deep) geo.setAttribute('aFloor', new THREE.Float32BufferAttribute(fl, 1));
  geo.computeVertexNormals();
  const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0, flatShading: true }));
  mesh.receiveShadow = true;
  mesh.name = 'crypt';
  const decor = new THREE.Group();
  decor.name = 'decor';
  const rnd = mulberry(seed ^ 0xc7);

  // Sconces and flames on room walls, a few per room, spread around.
  const flames: CryptBuild['flames'] = [];
  const sconceM = metal('#2b2a2e', { roughness: 0.5 });
  const flameM = emis('#ffcf7a', '#ff7a1a', 1.6, { transparent: true, opacity: 0.95, depthWrite: false });
  const violetM = emis('#e7c9ff', '#9b4dff', 1.8, { transparent: true, opacity: 0.95, depthWrite: false });
  for (let i = 0; i < L.rooms.length; i++) {
    const rm = L.rooms[i]!;
    const mine = walls.filter((w) => w.room === i);
    const want = isHall(i) ? 8 : i === 0 || rm.kind === 'entry' ? 2 : 4;
    const center = simToWorld(hexToWorld(rm.q, rm.r).x, hexToWorld(rm.q, rm.r).y);
    // Evenly by angle around the room's center.
    mine.sort((a, b) => Math.atan2(a.z - center.z, a.x - center.x) - Math.atan2(b.z - center.z, b.x - center.x));
    const sconces: THREE.Mesh[] = [];
    for (let k = 0; k < want && mine.length; k++) {
      const w = mine[Math.floor(((k + 0.5) * mine.length) / want)]!;
      const toward = new THREE.Vector3(center.x - w.x, 0, center.z - w.z).normalize();
      const p = new THREE.Vector3(w.x, 0.62, w.z).addScaledVector(toward, rad * 0.95);
      const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.035, 0.16, 6), sconceM);
      sc.position.copy(p);
      sconces.push(sc);
      const f = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.22, 7), isHall(i) && !furrows ? violetM : flameM);
      f.position.copy(p).setY(p.y + 0.17);
      decor.add(f);
      const glow = new THREE.Sprite(glowMat(isHall(i) && !furrows ? '#9b4dff' : '#ff9a3a', 0.7, true));
      glow.scale.setScalar(0.9);
      glow.position.copy(f.position);
      decor.add(glow);
      flames.push({ mesh: f, glow, room: i });
    }
    // A room's sconces stand still: one mesh.
    if (sconces.length) decor.add(oneMesh(sconces));
  }

  // Bones, skulls, rubble and candles on the floor, near the walls.
  const m4 = new THREE.Matrix4();
  const q4 = new THREE.Quaternion();
  const e = new THREE.Euler();
  const lists = { bones: [] as THREE.Matrix4[], skulls: [] as THREE.Matrix4[], rubble: [] as THREE.Matrix4[], candles: [] as THREE.Matrix4[], wicks: [] as THREE.Matrix4[], roots: [] as THREE.Matrix4[], moss: [] as THREE.Matrix4[] };
  const put = (list: THREE.Matrix4[], x: number, y: number, z: number, s: number, sy = s, flat = false): void => {
    e.set(flat ? Math.PI / 2 : (rnd() - 0.5) * 0.3, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.3);
    q4.setFromEuler(e);
    m4.compose(new THREE.Vector3(x, y, z), q4, new THREE.Vector3(s, sy, s));
    list.push(m4.clone());
  };
  for (const w of walls) {
    const toward = (dq: number, dr: number): THREE.Vector3 => {
      const c = hexToWorld(w.q + dq, w.r + dr);
      return simToWorld(c.x, c.y);
    };
    const d = DIRS.find((dd) => isFloor(w.q + dd.q, w.r + dd.r));
    if (!d) continue;
    const at = toward(d.q, d.r).lerp(new THREE.Vector3(w.x, 0, w.z), 0.32);
    const j = (): number => (rnd() - 0.5) * 0.5;
    const roll = rnd();
    if (furrows) {
      // The furrows: roots hanging down the wall's face, moss over its lip, stones fallen at its foot.
      const face = toward(d.q, d.r).sub(new THREE.Vector3(w.x, 0, w.z)).normalize();
      const side = new THREE.Vector3(-face.z, 0, face.x);
      const top = w.h ?? 1.3;
      const lip = new THREE.Vector3(w.x, 0, w.z).addScaledVector(face, rad * 0.86);
      if (roll < 0.55) {
        for (let k = 0; k < 2 + Math.floor(rnd() * 3); k++) {
          const len = 0.3 + rnd() * 0.6;
          const p = lip.clone().addScaledVector(side, (rnd() - 0.5) * rad * 1.1);
          put(lists.roots, p.x, top - len / 2, p.z, 0.016 + rnd() * 0.01, len);
        }
      }
      if (rnd() < 0.6) for (let k = 0; k < 2; k++) {
        const p = lip.clone().addScaledVector(side, (rnd() - 0.5) * rad).addScaledVector(face, -0.05);
        put(lists.moss, p.x, top - 0.02, p.z, 0.12 + rnd() * 0.1, 0.06 + rnd() * 0.05);
      }
      if (rnd() < 0.12) for (let k = 0; k < 2; k++) put(lists.rubble, at.x + j() * 0.6, 0.03, at.z + j() * 0.6, 0.04 + rnd() * 0.05);
      continue;
    }
    if (roll < 0.22) for (let k = 0; k < 3; k++) put(lists.rubble, at.x + j(), 0.04, at.z + j(), 0.06 + rnd() * 0.08);
    else if (roll < 0.36) {
      put(lists.skulls, at.x + j() * 0.5, 0.06, at.z + j() * 0.5, 0.08);
      for (let k = 0; k < 2; k++) put(lists.bones, at.x + j(), 0.03, at.z + j(), 0.025, 0.28, true);
    } else if (roll < 0.46) {
      for (let k = 0; k < 3; k++) {
        const x = at.x + j() * 0.6;
        const z = at.z + j() * 0.6;
        const h = 0.1 + rnd() * 0.14;
        put(lists.candles, x, h / 2, z, 0.035, h);
        m4.compose(new THREE.Vector3(x, h + 0.02, z), q4.identity(), new THREE.Vector3(1, 1, 1));
        lists.wicks.push(m4.clone());
      }
    }
  }
  const inst = (geo: THREE.BufferGeometry, mat: THREE.Material, list: THREE.Matrix4[]): void => {
    if (!list.length) return;
    const im = new THREE.InstancedMesh(geo, mat, list.length);
    list.forEach((mm, i) => im.setMatrixAt(i, mm));
    im.instanceMatrix.needsUpdate = true;
    decor.add(im);
  };
  const boneM = std('#cfc3a4', { roughness: 0.85 });
  inst(new THREE.CylinderGeometry(1, 0.8, 1, 5), boneM, lists.bones);
  inst(new THREE.SphereGeometry(1, 7, 6), boneM, lists.skulls);
  inst(new THREE.DodecahedronGeometry(1, 0), stone('#3a3a3e'), lists.rubble);
  inst(new THREE.CylinderGeometry(1, 1, 1, 6), std('#d8d0bd', { roughness: 0.7 }), lists.candles);
  inst(new THREE.SphereGeometry(0.025, 5, 4), emis('#fff2b0', '#ffb347', 2), lists.wicks);
  inst(new THREE.CylinderGeometry(0.6, 1, 1, 4), std('#2a2119', { roughness: 0.95, flatShading: true }), lists.roots);
  inst(new THREE.DodecahedronGeometry(1, 0), std('#2f3d24', { roughness: 0.95, flatShading: true }), lists.moss);

  // Portcullises in the corridors.
  const doors: CryptDoor[] = [];
  const barM = metal('#3a3b40', { roughness: 0.45 });
  const frameM = stone('#4a4c53');
  L.doors.forEach((d, i) => {
    const next = L.rooms[i + 1];
    // The deep crypt: the "door" into a floor's entrance is only a placeholder (the floors are not joined).
    if (next?.kind === 'entry') return;
    const c = hexToWorld(d.q, d.r);
    const p = simToWorld(c.x, c.y);
    // Facing the room it opens into: the next in a crypt's chain; in an expedition, the room whose edge it touches.
    // In the Furrows the rooms are only the rope, the camps and the hall: a door stands across its corridor, facing
    // one of the two crossings it joins (the even cells on either side of it, both floor).
    const across = DIRS.map((o) => ({ q: d.q + o.q, r: d.r + o.r })).find((o) => o.q % 2 === 0 && o.r % 2 === 0 && isFloor(o.q, o.r) && isFloor(2 * d.q - o.q, 2 * d.r - o.r));
    const into = furrows ? (across ?? d) : next ? (hexDist(d, next) === next.radius + 1 ? next : (L.rooms.find((rm) => hexDist(d, rm) === rm.radius + 1) ?? next)) : (DIRS.map((o) => ({ q: d.q + o.q, r: d.r + o.r })).find((o) => isFloor(o.q, o.r)) ?? d);
    const nc = hexToWorld(into.q, into.r);
    const yaw = Math.atan2(nc.x - c.x, nc.y - c.y);
    const frame = new THREE.Group();
    frame.position.copy(p);
    frame.rotation.y = yaw;
    const stones: THREE.Mesh[] = [];
    for (const k of [-1, 1]) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.4, 0.22), frameM);
      post.position.set(k * 0.55, 0.7, 0);
      stones.push(post);
    }
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.2, 0.26), frameM);
    lintel.position.y = 1.45;
    stones.push(lintel);
    frame.add(oneMesh(stones));
    // The bars rise as one (CryptDoor.bars): one mesh in their own group.
    const bars = new THREE.Group();
    const iron: THREE.Mesh[] = [];
    for (let k = -3; k <= 3; k++) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.045, 1.25, 0.045), barM);
      bar.position.set(k * 0.14, 0.63, 0);
      iron.push(bar);
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.08, 4), barM);
      tip.position.set(k * 0.14, 0, 0);
      tip.rotation.x = Math.PI;
      iron.push(tip);
    }
    for (const y of [0.3, 0.75, 1.15]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.05, 0.05), barM);
      rail.position.y = y;
      iron.push(rail);
    }
    bars.add(oneMesh(iron));
    frame.add(bars);
    const seal = new THREE.Sprite(glowMat('#ff4f3a', 0, true));
    seal.scale.setScalar(1.3);
    seal.position.set(0, 0.7, 0);
    frame.add(seal);
    decor.add(frame);
    doors.push({ i, bars, seal, pos: p, open: 0 });
  });

  // The Warden's rune circle (one per guardian's hall in the deep crypt; each circle is a child group).
  const runes = new THREE.Group();
  const runeM = new THREE.MeshBasicMaterial({ color: furrows ? '#ff4f3a' : '#9b4dff', transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, depthWrite: false });
  const ringGeos = ([[2.3, 2.38], [3.1, 3.16], [4.3, 4.34]] as const).map(([r0, r1]) => new THREE.RingGeometry(r0, r1, 72).rotateX(-Math.PI / 2));
  const glyphGeo = new THREE.PlaneGeometry(0.16, 0.42).rotateX(-Math.PI / 2);
  L.rooms.forEach((hall, i) => {
    if (!isHall(i)) return;
    const circle = new THREE.Group();
    circle.userData.room = i;
    const hc = hexToWorld(hall.q, hall.r);
    circle.position.copy(simToWorld(hc.x, hc.y)).setY(0.025);
    // Light added on light: one mesh draws the same as its rings and glyphs one by one.
    const marks: THREE.Mesh[] = ringGeos.map((rg) => new THREE.Mesh(rg.clone(), runeM));
    for (let k = 0; k < 12; k++) {
      const a = (k / 12) * Math.PI * 2;
      const glyph = new THREE.Mesh(glyphGeo.clone(), runeM);
      glyph.position.set(Math.cos(a) * 2.75, 0, Math.sin(a) * 2.75);
      glyph.rotation.y = -a;
      marks.push(glyph);
    }
    circle.add(oneMesh(marks));
    runes.add(circle);
  });
  for (const g of [...ringGeos, glyphGeo]) g.dispose();
  decor.add(runes);

  let exit: CryptBuild['exit'];
  if (exitHall) {
    const last = L.rooms[boss]!;
    const time = { value: 0 };
    const group = exitShaft(time);
    const c = hexToWorld(last.q, last.r);
    group.position.copy(simToWorld(c.x, c.y));
    const glow = new THREE.Sprite(glowMat('#dfe8ff', 0.55, true));
    glow.scale.setScalar(3.2);
    glow.position.set(0, 0.3, 0);
    group.add(glow);
    decor.add(group);
    exit = { group, time };
  }

  const roomLights = L.rooms.map((rm) => {
    const c = hexToWorld(rm.q, rm.r);
    return simToWorld(c.x, c.y).setY(rm.kind === 'boss' ? 4.2 : 3.2);
  });
  // What stands still keeps the matrix it was built with: three otherwise recomposes every object's matrix every
  // frame. What moves stays live: the bars (they rise), the flames (they flicker) and the rune circles (they turn).
  decor.traverse((o) => {
    o.updateMatrix();
    o.matrixAutoUpdate = false;
  });
  const live = [...doors.map((d) => d.bars), ...flames.map((f) => f.mesh), ...runes.children];
  for (const o of live) o.matrixAutoUpdate = true;
  // And what has nothing moving at or under it isn't even visited: its world matrix is set once here (the crypt
  // stands at the board's origin), and three's walk over the scene each frame skips it.
  decor.updateMatrixWorld(true);
  const moving = new Set<THREE.Object3D>();
  for (const o of live) for (let x: THREE.Object3D | null = o; x; x = x.parent) moving.add(x);
  decor.traverse((o) => {
    if (!moving.has(o) && !live.some((l) => isUnder(o, l))) o.matrixWorldAutoUpdate = false;
  });
  return { mesh, terrain, heights, decor, doors, flames, roomLights, runes, floors, deep, ...(exit ? { exit } : {}) };
};
